import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pracaService, Praca } from '../services/praca';

const PracaVisualizar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: praca, isLoading, error } = useQuery({
    queryKey: ['praca', id],
    queryFn: () => pracaService.getById(Number(id)),
    enabled: !!id,
  });

  const handleBack = () => {
    navigate('/pracas');
  };

  const handleEdit = () => {
    navigate(`/pracas/editar/${id}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Erro ao carregar os dados da praça.
        </Alert>
      </Box>
    );
  }

  if (!praca) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Praça não encontrada.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Visualizar Praça
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
          color="primary"
        >
          Editar
        </Button>
      </Box>

      {/* Conteúdo */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Informações Básicas */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Informações Básicas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Código
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {praca.id}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Nome da Praça
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {praca.nomePraca || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Informações do Funcionário */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Funcionário Vinculado
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Código do Funcionário
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {praca.codFuncionario || 'N/A'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Nome do Funcionário
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {praca.nomeFuncionario || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Data de Vínculo */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Data de Vínculo
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data de Vínculo Praça-Funcionário
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {praca.dataVinculo 
                    ? new Date(praca.dataVinculo).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    : 'N/A'
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
};

export default PracaVisualizar;
